import { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Upload, Music, Mic2, Headphones, Play, Pause,
  Loader2, Check, AlertCircle, Image as ImageIcon, Send, Heart,
  User, Mail, Tag, FileText, Award, Download, Edit3, X, Save, Trash2
} from 'lucide-react';
import useInlineAdmin from '../hooks/useInlineAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 50 * 1024 * 1024;
const MAX_DURATION = 600;

const DEFAULT_COVER_BLACK = "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/ce9n02vn_LOGO%20BLACK.jpg";
const DEFAULT_COVER_WHITE = "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/dtxrk43z_LOGO%20WHITE.jpg";

function fmtDuration(sec) {
  if (!sec || sec <= 0) return "--:--";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function fmtSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

function CommunityUploadForm({ onUploaded }) {
  const [form, setForm] = useState({
    artist_name: '', email: '', title: '', genre: '', description: '', credits: '', website: '',
  });
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const audioInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const handleAudio = (file) => {
    setError('');
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError(`Fichier trop volumineux (${fmtSize(file.size)}). Max: 50 MB.`);
      return;
    }
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac'].includes(ext)) {
      setError('Format non supporte. Accepte: MP3, WAV, OGG, FLAC, M4A, AAC.');
      return;
    }
    const url = URL.createObjectURL(file);
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      if (audio.duration > MAX_DURATION) {
        setError(`Track trop longue (${fmtDuration(audio.duration)}). Max: 10 minutes.`);
        URL.revokeObjectURL(url);
        return;
      }
      setAudioDuration(audio.duration);
      setAudioFile(file);
      URL.revokeObjectURL(url);
    });
    audio.addEventListener('error', () => {
      setError('Impossible de lire ce fichier audio.');
      URL.revokeObjectURL(url);
    });
  };

  const handleCover = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError('Image trop grande (max 8 MB).');
      return;
    }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!audioFile) return setError('Selectionne un fichier audio.');
    if (!form.artist_name.trim()) return setError('Ton nom d\'artiste est requis.');
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) return setError('Email invalide.');
    if (!form.title.trim()) return setError('Le titre est requis.');

    setSubmitting(true);
    setProgress(0);

    const fd = new FormData();
    fd.append('artist_name', form.artist_name);
    fd.append('email', form.email);
    fd.append('title', form.title);
    fd.append('genre', form.genre);
    fd.append('description', form.description);
    fd.append('credits', form.credits);
    fd.append('website', form.website || ''); // honeypot — must stay empty
    fd.append('audio_file', audioFile);
    if (coverFile) fd.append('cover_file', coverFile);

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/community/upload`);
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
      };
      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
          } else {
            try {
              const r = JSON.parse(xhr.responseText);
              reject(new Error(r.detail || 'Upload echoue'));
            } catch {
              reject(new Error('Upload echoue'));
            }
          }
        };
        xhr.onerror = () => reject(new Error('Erreur reseau'));
        xhr.send(fd);
      });
      setSuccess(true);
      onUploaded && onUploaded(result);
      setForm({ artist_name: '', email: '', title: '', genre: '', description: '', credits: '' });
      setAudioFile(null);
      setCoverFile(null);
      if (coverPreview) URL.revokeObjectURL(coverPreview);
      setCoverPreview(null);
      setAudioDuration(0);
    } catch (err) {
      setError(err.message || 'Une erreur est survenue.');
    } finally {
      setSubmitting(false);
      setProgress(0);
    }
  };

  if (success) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-yellow-900/30 via-zinc-900 to-zinc-950 border border-yellow-500/30 rounded-3xl p-8 text-center"
        data-testid="community-upload-success">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500/20 flex items-center justify-center">
          <Check size={32} className="text-yellow-400" />
        </div>
        <h3 className="text-2xl font-black text-white mb-2">Track envoye !</h3>
        <p className="text-zinc-400 mb-6">L'equipe KAZAJAH valide ton morceau sous 48h. Tu recevras un email.</p>
        <button onClick={() => setSuccess(false)} data-testid="community-upload-another"
          className="px-6 py-3 bg-yellow-500 text-black font-bold rounded-full hover:bg-yellow-400 transition-colors">
          Envoyer un autre track
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="community-upload-form">
      {/* Honeypot — invisible to humans, bots fill it and get rejected by backend */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
        <label>
          Ne pas remplir
          <input type="text" tabIndex={-1} autoComplete="off" name="website"
            value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">
            <User size={12} /> Nom d'artiste *
          </span>
          <input type="text" value={form.artist_name}
            onChange={(e) => setForm({ ...form, artist_name: e.target.value })}
            className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-xl text-white focus:outline-none focus:border-yellow-500"
            placeholder="DJ, Pseudo ou groupe"
            data-testid="community-input-artist"
            required />
        </label>
        <label className="block">
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">
            <Mail size={12} /> Email *
          </span>
          <input type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-xl text-white focus:outline-none focus:border-yellow-500"
            placeholder="ton@email.com"
            data-testid="community-input-email"
            required />
        </label>
        <label className="block">
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">
            <Music size={12} /> Titre *
          </span>
          <input type="text" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-xl text-white focus:outline-none focus:border-yellow-500"
            placeholder="Titre du track"
            data-testid="community-input-title"
            required />
        </label>
        <label className="block">
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">
            <Tag size={12} /> Genre
          </span>
          <input type="text" value={form.genre}
            onChange={(e) => setForm({ ...form, genre: e.target.value })}
            className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-xl text-white focus:outline-none focus:border-yellow-500"
            placeholder="Ex: Elektro Ka, Reggae, House..."
            data-testid="community-input-genre" />
        </label>
      </div>

      <label className="block">
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">
          <FileText size={12} /> Description
        </span>
        <textarea value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3}
          className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-xl text-white focus:outline-none focus:border-yellow-500 resize-none"
          placeholder="Histoire derriere le track, ambiance, inspiration..."
          data-testid="community-input-description" />
      </label>

      <label className="block">
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">
          <Award size={12} /> Credits
        </span>
        <input type="text" value={form.credits}
          onChange={(e) => setForm({ ...form, credits: e.target.value })}
          className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-xl text-white focus:outline-none focus:border-yellow-500"
          placeholder="Ex: feat. ARTISTE, prod. PRODUCER, mix MIX_ENG..."
          data-testid="community-input-credits" />
      </label>

      {/* File pickers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Audio */}
        <div>
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">
            <Mic2 size={12} /> Fichier audio * (max 50 MB / 10 min)
          </span>
          <div onClick={() => audioInputRef.current?.click()}
            className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-colors p-5 text-center ${
              audioFile ? 'border-yellow-500 bg-yellow-500/5' : 'border-zinc-800 hover:border-yellow-500/50 bg-zinc-900/50'
            }`}
            data-testid="community-audio-picker">
            <input ref={audioInputRef} type="file" accept="audio/*,.mp3,.wav,.ogg,.flac,.m4a,.aac"
              onChange={(e) => handleAudio(e.target.files?.[0])} className="hidden" />
            {audioFile ? (
              <div>
                <Music size={24} className="text-yellow-400 mx-auto mb-2" />
                <p className="text-sm font-bold text-white truncate">{audioFile.name}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {fmtSize(audioFile.size)} {audioDuration > 0 && `• ${fmtDuration(audioDuration)}`}
                </p>
              </div>
            ) : (
              <div>
                <Upload size={24} className="text-zinc-500 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">Choisir un fichier audio</p>
                <p className="text-xs text-zinc-600 mt-1">MP3, WAV, OGG, FLAC, M4A, AAC</p>
              </div>
            )}
          </div>
        </div>

        {/* Cover */}
        <div>
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">
            <ImageIcon size={12} /> Cover (optionnel)
          </span>
          <div onClick={() => coverInputRef.current?.click()}
            className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-colors p-5 text-center overflow-hidden ${
              coverPreview ? 'border-amber-500 bg-amber-500/5' : 'border-zinc-800 hover:border-amber-500/50 bg-zinc-900/50'
            }`}
            data-testid="community-cover-picker">
            <input ref={coverInputRef} type="file" accept="image/*"
              onChange={(e) => handleCover(e.target.files?.[0])} className="hidden" />
            {coverPreview ? (
              <div>
                <img src={coverPreview} alt="cover preview" className="w-20 h-20 object-cover rounded-lg mx-auto mb-2" />
                <p className="text-xs text-zinc-400 truncate">{coverFile?.name}</p>
              </div>
            ) : (
              <div>
                <ImageIcon size={24} className="text-zinc-500 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">Ajouter une cover</p>
                <p className="text-xs text-zinc-600 mt-1">JPG, PNG, WEBP (max 8 MB)</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl"
          data-testid="community-upload-error">
          <AlertCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      <button type="submit" disabled={submitting || !audioFile}
        data-testid="community-submit-button"
        className="w-full py-4 bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 text-black font-black rounded-2xl text-base hover:scale-[1.01] active:scale-[0.99] transition-transform disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-yellow-500/20">
        {submitting ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Envoi... {progress > 0 && `${progress}%`}
          </>
        ) : (
          <>
            <Send size={18} /> Soumettre pour validation
          </>
        )}
      </button>
      {submitting && progress > 0 && (
        <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
    </form>
  );
}

function CommunityTrackCard({ track, isPlaying, onPlay, isAdmin, adminPwd, onEdit, onDelete }) {
  const cover = track.cover_filename
    ? `${API}/uploads/${track.cover_filename}`
    : (parseInt(track.id?.slice(0, 2), 16) % 2 === 0 ? DEFAULT_COVER_BLACK : DEFAULT_COVER_WHITE);

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!isAdmin || !adminPwd) return;
    const url = `${API}/admin/community/tracks/${track.id}/download?admin_password=${encodeURIComponent(adminPwd)}`;
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.download = `${track.artist_name || 'KAZAJAH'} - ${track.title || 'track'}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900 border border-zinc-800 hover:border-yellow-500/40 transition-colors rounded-2xl overflow-hidden group"
      data-testid={`community-track-card-${track.id}`}>
      <div className="relative aspect-square overflow-hidden">
        <img src={cover} alt={track.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <button onClick={() => onPlay(track)}
          data-testid={`community-track-play-${track.id}`}
          className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="w-14 h-14 rounded-full bg-yellow-500 text-black flex items-center justify-center shadow-2xl">
            {isPlaying ? <Pause size={22} /> : <Play size={22} className="ml-1" />}
          </span>
        </button>
        {track.genre && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/70 backdrop-blur-sm text-yellow-400 text-[10px] font-bold uppercase tracking-wider rounded-full">
            {track.genre}
          </span>
        )}
        {isAdmin && (
          <div className="absolute top-2 right-2 flex flex-col gap-1">
            <button
              data-testid={`community-track-edit-${track.id}`}
              onClick={(e) => { e.stopPropagation(); onEdit?.(track); }}
              title="Editer cette track (admin)"
              className="p-1.5 rounded-full bg-black/70 backdrop-blur-sm text-blue-300 hover:bg-blue-500 hover:text-white transition-colors">
              <Edit3 size={13} />
            </button>
            <button
              data-testid={`community-track-download-${track.id}`}
              onClick={handleDownload}
              title="Telecharger l'audio (admin)"
              className="p-1.5 rounded-full bg-black/70 backdrop-blur-sm text-emerald-300 hover:bg-emerald-500 hover:text-white transition-colors">
              <Download size={13} />
            </button>
            <button
              data-testid={`community-track-delete-${track.id}`}
              onClick={(e) => { e.stopPropagation(); onDelete?.(track); }}
              title="Supprimer cette track (admin)"
              className="p-1.5 rounded-full bg-black/70 backdrop-blur-sm text-red-300 hover:bg-red-500 hover:text-white transition-colors">
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-bold text-white text-sm truncate">{track.title}</h3>
        <p className="text-xs text-zinc-400 truncate">{track.artist_name}</p>
        {track.credits && <p className="text-[10px] text-zinc-600 truncate mt-1">{track.credits}</p>}
        <div className="flex items-center justify-between mt-2 text-[10px] text-zinc-500">
          <span>{fmtDuration(track.duration)}</span>
          <span className="flex items-center gap-1">
            <Headphones size={10} /> {((track.plays || 0) + 1111).toLocaleString('fr-FR')} plays
          </span>
        </div>
      </div>
    </motion.div>
  );
}

/* ────── Inline edit modal for admin (same fields as AdminDashboard) ────── */
function CommunityInlineEditModal({ track, adminPwd, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: track.title || '',
    artist_name: track.artist_name || '',
    genre: track.genre || '',
    credits: track.credits || '',
    description: track.description || '',
    price: track.price ?? 0,
    plays: track.plays ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => { try { titleRef.current?.focus(); } catch (_e) { /* noop */ } }, 150);
    return () => clearTimeout(t);
  }, []);

  const titleMissing = !form.title?.trim();
  const showTitleError = submitAttempted && titleMissing;

  const save = async () => {
    setSubmitAttempted(true);
    if (titleMissing) { try { titleRef.current?.focus(); } catch (_e) { /* noop */ } return; }
    setError(''); setSaving(true);
    try {
      const r = await fetch(`${API}/admin/community/${track.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          price: parseFloat(form.price) || 0,
          password: adminPwd,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d.detail || `Erreur (${r.status})`);
        setSaving(false);
        return;
      }
      onSaved?.();
    } catch (e) {
      setError(e.message || 'Erreur reseau');
    }
    setSaving(false);
  };

  const lbl = 'text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block';
  const inp = 'w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      onClick={onClose}
      className="fixed inset-0 z-[140] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3"
      data-testid="community-inline-edit-modal">
      <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-zinc-950 border border-blue-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="px-5 py-3.5 bg-gradient-to-r from-blue-500/15 to-transparent border-b border-zinc-800 flex items-center justify-between">
          <div>
            <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest">Admin · Community</p>
            <h3 className="text-white font-black">Editer cette track</h3>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <label className={lbl}>Titre *</label>
            <input ref={titleRef} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={showTitleError ? `${inp} border-red-500 ring-2 ring-red-500/40` : inp}
              data-testid="community-edit-title" />
            {showTitleError && <p className="text-red-400 text-[11px] mt-1 font-semibold">⚠ Le titre est obligatoire.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Artiste</label>
              <input value={form.artist_name} onChange={(e) => setForm({ ...form, artist_name: e.target.value })} className={inp} />
            </div>
            <div>
              <label className={lbl}>Genre</label>
              <input value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} className={inp} placeholder="Ex: Elektro Ka" />
            </div>
          </div>
          <div>
            <label className={lbl}>Credits</label>
            <input value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} className={inp} placeholder="Prod by …, mix by …" />
          </div>
          <div>
            <label className={lbl}>Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={`${inp} resize-none`} placeholder="Contexte, inspiration…" />
          </div>
          <div>
            <label className={lbl}>Prix (0 = gratuit)</label>
            <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inp} />
          </div>
          {error && <p className="text-red-400 text-xs" data-testid="community-edit-error">{error}</p>}
        </div>

        <div className="px-5 py-3.5 bg-zinc-950 border-t border-zinc-800 flex items-center justify-end gap-2">
          {submitAttempted && titleMissing && (
            <p className="mr-auto text-red-400 text-xs font-bold" data-testid="community-edit-footer-error">
              ⚠ Renseigne le "Titre" pour continuer
            </p>
          )}
          <button onClick={onClose} className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-sm">Annuler</button>
          <button onClick={save} disabled={saving}
            data-testid="community-edit-save"
            title={titleMissing ? 'Renseigne le Titre' : 'Sauvegarder'}
            className="px-5 py-2 bg-blue-500 hover:bg-blue-400 disabled:opacity-60 text-white font-black rounded-lg text-sm flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Sauvegarder
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function CommunityPage({ onBack }) {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [editingTrack, setEditingTrack] = useState(null);
  const audioRef = useRef(null);
  const formRef = useRef(null);
  const { isAdmin, adminPwd } = useInlineAdmin();

  const fetchTracks = useCallback(async () => {
    try {
      const res = await fetch(`${API}/community/tracks`);
      const data = await res.json();
      setTracks(Array.isArray(data) ? data : []);
    } catch {
      setTracks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTracks(); }, [fetchTracks]);

  const handlePlay = (track) => {
    if (currentTrack?.id === track.id) {
      if (audioRef.current?.paused) audioRef.current.play();
      else audioRef.current?.pause();
    } else {
      setCurrentTrack(track);
      setTimeout(() => audioRef.current?.play(), 50);
    }
  };

  const handleDelete = async (track) => {
    if (!window.confirm(`Supprimer definitivement "${track.title}" ?\nCette action est irreversible.`)) return;
    try {
      const r = await fetch(`${API}/admin/community/${track.id}?password=${encodeURIComponent(adminPwd)}`, { method: 'DELETE' });
      if (!r.ok && r.status !== 204) {
        const d = await r.json().catch(() => ({}));
        alert('Erreur suppression : ' + (d.detail || r.status));
        return;
      }
      if (currentTrack?.id === track.id) {
        audioRef.current?.pause();
        setCurrentTrack(null);
      }
      fetchTracks();
    } catch (e) {
      alert('Erreur reseau : ' + e.message);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="community-page">
      {/* Hero header */}
      <div className="relative overflow-hidden border-b border-zinc-900">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-900/25 via-amber-900/15 to-black" />
        <div className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(234,179,8,0.25), transparent 50%), radial-gradient(circle at 80% 70%, rgba(249,115,22,0.2), transparent 50%)',
          }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-8 pt-24 pb-12">
          <button onClick={onBack} data-testid="community-back-button"
            className="flex items-center gap-2 text-zinc-400 hover:text-yellow-400 transition-colors mb-6 text-sm font-medium">
            <ArrowLeft size={16} /> Retour au site
          </button>
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 bg-yellow-500/15 border border-yellow-500/30 text-yellow-300 text-xs font-bold uppercase tracking-wider rounded-full mb-4">
              KAZAJAH Community
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 leading-[1.05]">
              Partage ton son.<br />
              <span className="bg-gradient-to-r from-yellow-400 via-amber-300 to-orange-400 bg-clip-text text-transparent">
                Le label valide.
              </span>
            </h1>
            <p className="text-zinc-400 text-base sm:text-lg max-w-2xl">
              Uploade tes <span className="text-white font-semibold">tracks, loops ou voix</span>. Notre equipe revise sous 48h.
              Si valide, ton son rejoint la galerie publique <span className="text-yellow-400">KAZAJAH Community</span>.
            </p>
            <button onClick={() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              data-testid="community-cta-upload"
              className="mt-6 px-6 py-3 bg-yellow-500 text-black font-bold rounded-full hover:bg-yellow-400 transition-colors inline-flex items-center gap-2">
              <Upload size={16} /> Uploader maintenant
            </button>
          </div>
        </div>
      </div>

      {/* Approved community gallery */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black">Sons valides</h2>
            <p className="text-zinc-500 text-sm mt-1">
              {tracks.length} track{tracks.length !== 1 ? 's' : ''} approuve{tracks.length !== 1 ? 's' : ''} par le label
            </p>
          </div>
          {isAdmin && (
            <button onClick={async () => {
                if (!window.confirm('Ajouter +1111 plays a TOUTES les tracks Community ?')) return;
                try {
                  const r = await fetch(`${API}/admin/community/boost-plays`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ password: adminPwd, delta: 1111 })
                  });
                  const d = await r.json();
                  if (r.ok) {
                    alert(`+1111 plays ajoutes sur ${d.updated} tracks Community.`);
                    fetchTracks();
                  } else { alert(d.detail || 'Erreur'); }
                } catch (e) { alert('Erreur reseau'); }
              }}
              data-testid="community-boost-plays-btn"
              title="Ajoute +1111 plays a toutes les tracks Community en un clic"
              className="px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 transition-colors inline-flex items-center gap-1">
              +1111 plays a toutes
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-yellow-500" />
          </div>
        ) : tracks.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-zinc-800 rounded-3xl"
            data-testid="community-empty-state">
            <Heart size={32} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Aucun son valide pour le moment.</p>
            <p className="text-zinc-600 text-xs mt-1">Sois le premier a contribuer.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {tracks.map((t) => (
              <CommunityTrackCard key={t.id} track={t}
                isPlaying={currentTrack?.id === t.id}
                onPlay={handlePlay}
                isAdmin={isAdmin} adminPwd={adminPwd}
                onEdit={setEditingTrack}
                onDelete={handleDelete} />
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {editingTrack && (
          <CommunityInlineEditModal
            track={editingTrack}
            adminPwd={adminPwd}
            onClose={() => setEditingTrack(null)}
            onSaved={() => { setEditingTrack(null); fetchTracks(); }}
          />
        )}
      </AnimatePresence>

      {/* Upload form */}
      <section ref={formRef} className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-black">Uploade ton son</h2>
          <p className="text-zinc-500 text-sm mt-2">
            Tracks, loops, voix — tous les formats audio acceptes. Validation manuelle par l'equipe.
          </p>
        </div>
        <div className="bg-gradient-to-b from-zinc-900 to-zinc-950 border border-zinc-800 rounded-3xl p-6 sm:p-8">
          <CommunityUploadForm onUploaded={fetchTracks} />
        </div>
        <div className="text-center mt-6 text-xs text-zinc-600">
          En soumettant, tu confirmes posseder les droits sur le contenu audio uploade.
        </div>
      </section>

      {/* Sticky audio player */}
      <AnimatePresence>
        {currentTrack && (
          <motion.div initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
            className="fixed bottom-0 left-0 right-0 z-40 bg-zinc-950/95 backdrop-blur-lg border-t border-yellow-500/30 px-4 py-2.5"
            data-testid="community-mini-player">
            <audio ref={audioRef}
              src={`${API}/community/tracks/${currentTrack.id}/audio`}
              autoPlay
              onEnded={() => setCurrentTrack(null)} />
            <div className="max-w-6xl mx-auto flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                <Music size={14} className="text-black" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white truncate">{currentTrack.title}</p>
                <p className="text-[10px] text-zinc-400 truncate">{currentTrack.artist_name} — KAZAJAH Community</p>
              </div>
              <button onClick={() => handlePlay(currentTrack)}
                className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-black hover:scale-105 transition-transform">
                <Pause size={16} />
              </button>
              <button onClick={() => setCurrentTrack(null)}
                className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors ml-1">
                ✕
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export { CommunityUploadForm };
